import { Bell, Check, Save, Share2, Shield, Trash2, TriangleAlert, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePWA } from '../../contexts/PWAContext';
import { db } from '../../services/db';
import { captureSilentError } from '../../services/posthog';
import { useUIStore } from '../../stores/useUIStore';

interface SettingsTabProps {
  onOpenAdmin: () => void;
  notificationPermission: NotificationPermission;
  onRequestNotificationPermission: () => void;
  onClose: () => void;
}

const SettingsTabComponent: React.FC<SettingsTabProps> = ({
  onOpenAdmin,
  notificationPermission,
  onRequestNotificationPermission,
  onClose
}) => {
  const { currentUser, logout } = useAuth();
  const { checkForUpdate, updateCheckStatus, lastCheckTime, needRefresh, updateServiceWorker, appVersion } = usePWA();

  // Delete Account State
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Change Password State
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const handleDeleteAccount = async () => {
    if (!currentUser || deleteConfirmText !== currentUser.username) return;
      
    try {
        await db.deleteAccount();
        logout();
        onClose();
        useUIStore.getState().showToast("Account deleted successfully.", 'success');
    } catch (e) {
        useUIStore.getState().showToast("Failed to delete account: " + (e instanceof Error ? e.message : 'Unknown error'), 'error');
        captureSilentError('SettingsTab.handleDeleteAccount', e);
    }
  };

  const handleChangePassword = async () => {
      setPasswordError('');
      setPasswordSuccess('');

      if (newPassword.length < 6) {
          setPasswordError("Password must be at least 6 characters");
          return;
      }

      if (newPassword !== confirmNewPassword) {
          setPasswordError("Passwords do not match");
          return;
      }

      try {
          await db.updatePassword(newPassword);
          setPasswordSuccess("Password updated successfully!");
          setNewPassword('');
          setConfirmNewPassword('');
      } catch (e) {
          captureSilentError('SettingsTab.handleChangePassword', e);
          setPasswordError(e instanceof Error ? e.message : "Failed to update password");
      }
  };

  if (!currentUser) return null;

  return (
    <div className="p-6 space-y-6">
        
        {/* NOTIFICATIONS */}
        <div className="space-y-4">
             <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider flex items-center gap-2">
                <Bell size={14} />
                Notifications
             </h4>
             <div className="bg-white border border-brand-cream-dark p-4 rounded-xl flex items-center justify-between">
                 <div>
                     <p className="font-bold text-brand-dark text-sm">Push Notifications</p>
                     <p className="text-xs text-brand-dark-light font-medium">
                        {notificationPermission === 'granted' ? 'Enabled' : 
                         notificationPermission === 'denied' ? 'Blocked in Settings' : 'Enable updates'}
                     </p>
                 </div>

                 {notificationPermission === 'default' && (
                     <button 
                        onClick={onRequestNotificationPermission}
                        className="px-4 py-2 bg-brand-dark text-white rounded-lg text-xs font-bold shadow hover:bg-stone-700 transition"
                     >
                        Enable
                     </button>
                 )}
                 
                 {notificationPermission === 'granted' && (
                     <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold border border-green-200">
                         On
                     </span>
                 )}

                {notificationPermission === 'denied' && (
                     <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold border border-red-200">
                         Off
                     </span>
                 )}
             </div>
        </div>

        {/* Change Password */}
        <div className="space-y-4">
            <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider flex items-center gap-2">
                <Shield size={14} />
                Security
            </h4>
            <div className="bg-white border border-brand-cream-dark p-4 rounded-xl space-y-4">
                <div>
                    <label className="block text-xs font-bold text-brand-dark-light uppercase tracking-wider mb-1">New Password</label>
                    <input 
                        type="password" 
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        className="w-full p-3 bg-brand-cream border border-brand-cream-dark rounded-xl outline-none focus:border-brand-red font-semibold text-brand-dark"
                        placeholder="••••••••"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-brand-dark-light uppercase tracking-wider mb-1">Confirm New Password</label>
                    <input 
                        type="password" 
                        value={confirmNewPassword}
                        onChange={e => setConfirmNewPassword(e.target.value)}
                        className="w-full p-3 bg-brand-cream border border-brand-cream-dark rounded-xl outline-none focus:border-brand-red font-semibold text-brand-dark"
                        placeholder="••••••••"
                    />
                </div>
                
                {passwordError && <p className="text-brand-red text-xs font-bold bg-red-50 p-2 rounded-lg">{passwordError}</p>}
                {passwordSuccess && <p className="text-green-600 text-xs font-bold bg-green-50 p-2 rounded-lg">{passwordSuccess}</p>}

                <button 
                    onClick={handleChangePassword}
                    disabled={!newPassword || !confirmNewPassword}
                    className="w-full py-2 bg-brand-dark text-white rounded-lg font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-700 transition active:scale-95 text-sm"
                >
                    Update Password
                </button>
            </div>
        </div>

        {/* App Updates */}
        <div className="space-y-4">
             <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider flex items-center gap-2">
                <Check size={14} />
                App Updates
             </h4>
             <div className="bg-white border border-brand-cream-dark p-4 rounded-xl space-y-3">
                 <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-brand-dark">Current Version</span>
                    <span className="text-xs font-mono bg-brand-cream px-2 py-1 rounded text-brand-dark-light">v{appVersion}</span>
                 </div>
                 
                 {needRefresh ? (
                    <button
                        onClick={() => updateServiceWorker(true)}
                        className="w-full py-3 bg-brand-gold text-white font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg animate-pulse"
                    >
                        <Save size={18} />
                        Apply & Restart App
                    </button>
                ) : (
                    <>
                        <button
                            onClick={checkForUpdate}
                            disabled={updateCheckStatus === 'checking'}
                            className={`w-full py-2 border font-bold rounded-lg flex items-center justify-center gap-2 active:scale-95 transition-all text-sm disabled:opacity-80 disabled:cursor-wait
                            ${updateCheckStatus === 'up-to-date' ? 'bg-green-50 border-green-200 text-green-700' : 
                              updateCheckStatus === 'error' ? 'bg-red-50 border-red-200 text-brand-red' : 
                              'bg-white border-brand-cream-dark text-brand-dark'}`}
                        >
                            {updateCheckStatus === 'checking' ? (
                                    <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-brand-dark"></div>
                                    <span className="text-sm">Checking...</span>
                                    </>
                            ) : updateCheckStatus === 'up-to-date' ? (
                                    <>
                                        <Check size={16} />
                                        <span className="text-sm">Up to Date</span>
                                    </>
                            ) : updateCheckStatus === 'error' ? (
                                    <>
                                        <XCircle size={16} />
                                        <span className="text-sm">Check Failed</span>
                                    </>
                            ) : (
                                    <span className="text-sm">Check for Updates</span>
                            )}
                        </button>
                        {lastCheckTime && (
                           <p className="text-[10px] text-brand-dark-light text-center">
                               Last checked: {new Date(lastCheckTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                           </p>
                        )}
                    </>
                )}
             </div>
        </div>

        {/* Share App */}
        <div className="space-y-4">
             <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider flex items-center gap-2">
                <Share2 size={14} />
                Share
             </h4>
             <button
                onClick={async () => {
                    const shareData = {
                        title: 'Good Shout',
                        text: 'Share your top recommendations with me on Good Shout',
                        url: window.location.origin,
                    };
                    try {
                        if (navigator.share) {
                            await navigator.share(shareData);
                        } else {
                            await navigator.clipboard.writeText(shareData.url);
                            useUIStore.getState().showToast('Link copied to clipboard!', 'success');
                        }
                    } catch (e) {
                        if (e instanceof Error && e.name === 'AbortError') return;
                        useUIStore.getState().showToast('Failed to share', 'error');
                    }
                }}
                className="w-full py-2 bg-white border border-brand-cream-dark text-brand-dark font-bold rounded-lg flex items-center justify-center gap-2 active:scale-95 transition-all text-sm hover:bg-brand-cream"
             >
                <Share2 size={16} />
                Share Good Shout
             </button>
        </div>

        {/* Admin */}
        {currentUser.isAdmin && (
            <div className="pt-2">
                 <button
                    onClick={onOpenAdmin}
                    className="w-full py-3 bg-brand-dark text-white font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg"
                >
                    <Shield size={20} />
                    Admin Panel
                </button>
            </div>
        )}

        {/* Danger Zone */}
        <div className="mt-8 pt-8 border-t border-brand-cream-dark">
            <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider mb-4 flex items-center gap-2">
                <TriangleAlert size={14} />
                Danger Zone
            </h4>
            
            {!isDeleteConfirmOpen ? (
                <button 
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    className="w-full py-3 border-2 border-brand-red text-brand-red rounded-xl font-bold hover:bg-red-50 transition active:scale-95 flex items-center justify-center gap-2"
                >
                    <Trash2 size={18} />
                    Delete Account
                </button>
            ) : (
                <div className="bg-red-50 p-4 rounded-xl border border-brand-red animate-in fade-in">
                    <p className="text-sm font-bold text-brand-dark mb-2">Are you sure? This cannot be undone.</p>
                    <p className="text-xs text-brand-dark-light mb-4">Type <span className="font-mono font-bold text-brand-dark">{currentUser.username}</span> to confirm.</p>
                    
                    <input 
                        type="text" 
                        value={deleteConfirmText}
                        onChange={e => setDeleteConfirmText(e.target.value)}
                        placeholder={currentUser.username}
                        className="w-full p-2 bg-white border border-brand-red/30 rounded-lg mb-3 text-sm font-mono"
                    />
                    
                    <div className="flex gap-2">
                        <button 
                            onClick={() => { setIsDeleteConfirmOpen(false); setDeleteConfirmText(''); }}
                            className="flex-1 py-2 bg-white border border-brand-cream-dark text-brand-dark font-bold rounded-lg text-sm"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleDeleteAccount}
                            disabled={deleteConfirmText !== currentUser.username}
                            className="flex-1 py-2 bg-brand-red text-white font-bold rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Delete Forever
                        </button>
                    </div>
                </div>
            )}
        </div>


    </div>
  );
};
export const SettingsTab = React.memo(SettingsTabComponent);
